import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { quickTourSteps, QuickTourStep } from '../data/quickTourSteps';
import { hasRouteAccess } from '../routes/rolePermissions';

interface TourContextValue {
  isTourOpen: boolean;
  currentStep: number;
  steps: QuickTourStep[];
  currentStepData: QuickTourStep | null;
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

const ROUTE_BY_TARGET: Record<string, string> = {
  'tour-nav-home': '/home',
  'tour-nav-dashboard': '/dashboard',
  'tour-nav-settings': '/application-settings',
  'tour-nav-api-keys': '/api-keys',
  'tour-nav-members': '/members',
  'tour-nav-participants': '/application-participants',
  'tour-nav-agents': '/application-agents',
  'tour-nav-chat-bot': '/chat-bot',
  'tour-nav-call-recordings': '/call-recordings',
  'tour-nav-documentation': '/documentation',
  'tour-upgrade-plan': '/plans-and-billing',
  'tour-support-center': '/support',
};

function getAvailableSteps(role: string): QuickTourStep[] {
  return quickTourSteps.filter((step) => {
    if (step.path && !hasRouteAccess(role, step.path)) {
      return false;
    }
    const route = ROUTE_BY_TARGET[step.target];
    if (route && !hasRouteAccess(role, route)) {
      return false;
    }
    return true;
  });
}

export function TourProvider({ children }: React.PropsWithChildren<{}>): React.ReactElement {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const role = userDetails?.role || '';
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSteps, setActiveSteps] = useState<QuickTourStep[]>(() => getAvailableSteps(role));

  const startTour = useCallback(() => {
    setActiveSteps(getAvailableSteps(role));
    setCurrentStep(0);
    setIsTourOpen(true);
  }, [role]);

  const stopTour = useCallback(() => {
    setIsTourOpen(false);
    setCurrentStep(0);
    localStorage.setItem('sampark_tour_completed', 'true');
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev >= activeSteps.length - 1) {
        setIsTourOpen(false);
        localStorage.setItem('sampark_tour_completed', 'true');
        return 0;
      }
      return prev + 1;
    });
  }, [activeSteps.length]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < activeSteps.length) {
      setCurrentStep(index);
    }
  }, [activeSteps.length]);

  const value = useMemo<TourContextValue>(
    () => ({
      isTourOpen,
      currentStep,
      steps: activeSteps,
      currentStepData: isTourOpen ? activeSteps[currentStep] ?? null : null,
      startTour,
      stopTour,
      nextStep,
      prevStep,
      goToStep,
    }),
    [isTourOpen, currentStep, activeSteps, startTour, stopTour, nextStep, prevStep, goToStep]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
